import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  TrashIcon,
  PlusCircleIcon,
  XMarkIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';

const ProjectPlanDetail = ({ clientId, planId, clientName, onBack, onDelete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingRowId, setSavingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [allFieldDefinitions, setAllFieldDefinitions] = useState([]);  // Store all available fields
  const [planFieldDefinitions, setPlanFieldDefinitions] = useState([]); // Store only fields for this plan
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [headerFormData, setHeaderFormData] = useState({ name: '', description: '' });
  const saveTimeoutRef = useRef(null);
  const [addingRow, setAddingRow] = useState(false);

  useEffect(() => {
    fetchData();
  }, [clientId, planId]);

  // Debug effect to log field definitions after they're set
  useEffect(() => {
    if (fieldDefinitions.length > 0) {
      console.log('Current fieldDefinitions order in component:', 
        fieldDefinitions.map(f => ({ 
          id: f.id, 
          name: f.name, 
          planOrder: f.planDisplayOrder,
          regularOrder: f.display_order
        }))
      );
    }
  }, [fieldDefinitions]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [fieldsRes, planRes] = await Promise.all([
        api.get('/settings/project-fields'),
        api.get(`/clients/${clientId}/project-plans/${planId}`)
      ]);

      if (!fieldsRes.data.success) throw new Error(fieldsRes.data.message);
      if (!planRes.data.success) throw new Error(planRes.data.message);

      // Store all available field definitions
      setAllFieldDefinitions(fieldsRes.data.fields);
      
      // Set the plan data
      const planData = planRes.data.plan;
      setPlan(planData);
      
      // Create a map of field definition IDs to their definition objects
      const fieldDefMap = fieldsRes.data.fields.reduce((map, field) => {
        map[field.id] = field;
        return map;
      }, {});
      
      // Get the list of field IDs that are used in this plan
      let planFieldIds = [];
      
      // First try to get field IDs from plan's field_definitions
      if (planData.field_definitions && planData.field_definitions.length > 0) {
        planFieldIds = planData.field_definitions.map(pf => pf.field_definition_id);
      } 
      // Fallback: extract field IDs from field values in rows
      else if (planData.rows && planData.rows.length > 0) {
        planFieldIds = [...new Set(
          planData.rows.flatMap(row => 
            row.field_values.map(fv => fv.field_definition_id)
          )
        )];
      }
      
      // Create the complete field definitions with fields from the plan
      const planSpecificFields = planFieldIds
        .map(fieldId => {
          const fullField = fieldDefMap[fieldId];
          if (!fullField) return null;
          
          return fullField;
        })
        .filter(field => field !== null);
      
      // Sort fields by their display_order from ProjectFields.js
      const sortedFields = [...planSpecificFields].sort((a, b) => 
        (a.display_order || 999) - (b.display_order || 999)
      );
      
      setPlanFieldDefinitions(sortedFields);
      setFieldDefinitions(sortedFields);
      
      setHeaderFormData({
        name: planData.name,
        description: planData.description || ''
      });
      
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  const handleHeaderInputChange = (e) => {
    const { name, value } = e.target;
    setHeaderFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveHeader = async (e) => {
    e.preventDefault();
    
    if (!headerFormData.name.trim()) {
      return toast.error('Plan name is required');
    }

    try {
      setLoading(true);
      
      const payload = { 
        name: headerFormData.name, 
        description: headerFormData.description 
      };
      
      const response = await api.patch(
        `/clients/${clientId}/project-plans/${planId}`,
        payload
      );

      if (!response.data.success) throw new Error(response.data.message);

      setPlan(prev => ({
        ...prev,
        name: headerFormData.name,
        description: headerFormData.description
      }));
      
      setIsEditingHeader(false);
      toast.success('Plan details updated successfully');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to update plan details');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldValueChange = async (row, fieldId, value) => {
    // Update local state immediately for responsiveness
    const updatedPlan = { ...plan };
    const rowIndex = updatedPlan.rows.findIndex(r => r.id === row.id);
    
    if (rowIndex === -1) return;
    
    // Find the field value in the row
    const fieldIndex = updatedPlan.rows[rowIndex].field_values.findIndex(
      fv => fv.field_definition_id === fieldId
    );
    
    if (fieldIndex === -1) {
      // Add new field value
      updatedPlan.rows[rowIndex].field_values.push({
        field_definition_id: fieldId,
        value_text: value
      });
    } else {
      // Update existing field value
      updatedPlan.rows[rowIndex].field_values[fieldIndex] = {
        ...updatedPlan.rows[rowIndex].field_values[fieldIndex],
        value_text: value
      };
    }
    
    setPlan(updatedPlan);
    
    // Debounce the API call to prevent too many requests
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(() => {
      saveRowUpdate(row.id, fieldId, value);
    }, 3000);
  };
  
  const saveRowUpdate = async (rowId, fieldId, value) => {
    try {
      setSavingRowId(rowId);
      
      const fieldValues = [
        { 
          field_definition_id: fieldId, 
          value_text: value
        }
      ];
      
      const response = await api.patch(
        `/clients/${clientId}/project-plans/${planId}/rows/${rowId}`,
        { field_values: fieldValues }
      );
      
      if (!response.data.success) throw new Error(response.data.message);
      
      // Subtle success indicator
      toast.success('Saved', { duration: 1000 });
    } catch (err) {
      console.error('Error saving row update:', err);
      toast.error('Failed to save change');
      
      // Revert the change in local state
      await fetchData();
    } finally {
      setSavingRowId(null);
    }
  };

  const handleToggleRowCompleted = async (row) => {
    try {
      setSavingRowId(row.id);
      
      // Toggle completed status
      const newCompletedStatus = !(row.completed || false);
      
      // Update local state immediately for responsiveness
      const updatedPlan = { ...plan };
      const rowIndex = updatedPlan.rows.findIndex(r => r.id === row.id);
      
      if (rowIndex === -1) return;
      
      updatedPlan.rows[rowIndex].completed = newCompletedStatus;
      setPlan(updatedPlan);
      
      const response = await api.patch(
        `/clients/${clientId}/project-plans/${planId}/rows/${row.id}`,
        { completed: newCompletedStatus }
      );
      
      if (!response.data.success) throw new Error(response.data.message);
      
      toast.success(`Row marked as ${newCompletedStatus ? 'completed' : 'incomplete'}`);
    } catch (err) {
      console.error('Error updating row completion status:', err);
      toast.error('Failed to update row status');
      
      // Revert the change in local state
      await fetchData();
    } finally {
      setSavingRowId(null);
    }
  };

  const addRow = async () => {
    try {
      setAddingRow(true);
      
      // Create empty field values
      const initialFieldValues = fieldDefinitions.map(field => ({
        field_definition_id: field.id,
        value_text: ''
      }));
      
      const response = await api.post(
        `/clients/${clientId}/project-plans/${planId}/rows`,
        { field_values: initialFieldValues }
      );
      
      if (!response.data.success) throw new Error(response.data.message);
      
      // Add the new row to the local state
      const updatedPlan = { ...plan };
      updatedPlan.rows = [...updatedPlan.rows, response.data.row];
      setPlan(updatedPlan);
      
      toast.success('Row added');
    } catch (err) {
      console.error(err);
      toast.error('Failed to add row');
    } finally {
      setAddingRow(false);
    }
  };

  const removeRow = async (rowId) => {
    // Don't allow removing the last row
    if (plan.rows.length <= 1) {
      return toast.error('Cannot remove the last row');
    }
    
    if (!window.confirm('Are you sure you want to delete this row?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await api.delete(
        `/clients/${clientId}/project-plans/${planId}/rows/${rowId}`
      );
      
      if (!response.data.success) throw new Error(response.data.message);
      
      // Update local state
      const updatedPlan = { ...plan };
      updatedPlan.rows = updatedPlan.rows.filter(row => row.id !== rowId);
      
      setPlan(updatedPlan);
      toast.success('Row deleted successfully');
    } catch (err) {
      console.error('Error deleting row:', err);
      toast.error('Failed to delete row');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCompleted = async () => {
    try {
      const res = await api.patch(`/clients/${clientId}/project-plans/${planId}/toggle-completion`, {
        is_completed: !plan.is_completed
      });
      if (!res.data.success) throw new Error(res.data.message);

      // Update local state
      setPlan(prev => ({
        ...prev,
        is_completed: !prev.is_completed
      }));
      
      toast.success(`Marked plan as ${plan.is_completed ? 'incomplete' : 'completed'}`);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Error updating status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this project plan?')) return;

    try {
      const res = await api.delete(`/clients/${clientId}/project-plans/${planId}`);
      if (!res.data.success) throw new Error(res.data.message);
      toast.success('Deleted successfully');
      onDelete();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Error deleting project plan');
    }
  };

  const handleCreateProject = () => {
    if (!window.confirm('Create a project from this plan?')) return;
    
    // Instead of API call, redirect to AddProject page with query parameters
    navigate(`/projects/add?client=${clientId}&plan=${planId}`);
  };

  const getFieldValue = (row, fieldId) => {
    // Directly find the field value from the row's field_values array
    const fieldValue = row.field_values.find(fv => fv.field_definition_id === fieldId);
    // Return the value_text or empty string if not found
    return fieldValue ? fieldValue.value_text : '';
  };

  // Helper to get field values in the correct order
  const getOrderedFieldValues = (row) => {
    // Create a map of field definition IDs to their corresponding field values
    const fieldValueMap = {};
    row.field_values.forEach(value => {
      fieldValueMap[value.field_definition_id] = value;
    });
    
    // Return field values in the same order as fieldDefinitions
    return fieldDefinitions.map(field => ({
      field_definition_id: field.id,
      value_text: getFieldValue(row, field.id),
      field: field
    }));
  };

  const renderFieldInput = (row, fieldDef) => {
    const value = getFieldValue(row, fieldDef.id);
    const isRowSaving = savingRowId === row.id;
    const isCompleted = row.completed || false;
    
    if (fieldDef.field_type === 'dropdown') {
      return (
        <select 
          value={value} 
          onChange={e => handleFieldValueChange(row, fieldDef.id, e.target.value)}
          disabled={isRowSaving || isCompleted}
          className={`block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${isCompleted ? 'bg-gray-50' : ''}`}
        >
          <option value="">-- Select --</option>
          {fieldDef.options?.values?.map((opt, idx) => (
            <option key={idx} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    
    const inputType = 
      fieldDef.field_type === 'numeric' ? 'number' : 
      fieldDef.field_type === 'date' ? 'date' : 'text';
    
    return (
      <input 
        type={inputType} 
        value={value} 
        onChange={e => handleFieldValueChange(row, fieldDef.id, e.target.value)} 
        disabled={isRowSaving || isCompleted}
        className={`block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${isCompleted ? 'bg-gray-50' : ''}`}
      />
    );
  };

  if (loading && !plan) return <div className="text-center p-6">Loading...</div>;
  if (error) return <div className="text-center p-6 text-red-500">{error}</div>;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-indigo-600 hover:text-indigo-900"
          disabled={loading}
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" /> Back to plans
        </button>

        <div className="flex space-x-3">
          <button
            onClick={() => setIsEditingHeader(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
            disabled={loading || isEditingHeader}
          >
            <PencilIcon className="h-4 w-4 mr-2" /> Edit Plan Info
          </button>
          <button
            onClick={handleMarkCompleted}
            className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md ${plan?.is_completed ? 'text-gray-700 bg-gray-100 hover:bg-gray-200' : 'text-green-700 bg-green-100 hover:bg-green-200'}`}
            disabled={loading}
          >
            <CheckIcon className="h-4 w-4 mr-2" /> {plan?.is_completed ? 'Mark Incomplete' : 'Mark Completed'}
          </button>
          {plan && !plan.project_created && (
            <button
              onClick={handleCreateProject}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
              disabled={loading}
            >
              <PlusCircleIcon className="h-4 w-4 mr-2" /> Create Project
            </button>
          )}
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
            disabled={loading}
          >
            <TrashIcon className="h-4 w-4 mr-2" /> Delete
          </button>
        </div>
      </div>

      {/* Plan Header Information */}
      {isEditingHeader ? (
        <form onSubmit={handleSaveHeader} className="mb-8 p-4 bg-gray-50 rounded-md">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Plan Name *</label>
              <input
                type="text"
                name="name"
                value={headerFormData.name}
                onChange={handleHeaderInputChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={headerFormData.description}
                onChange={handleHeaderInputChange}
                rows="3"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsEditingHeader(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      ) : (
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">{plan?.name}</h2>
          <p className="text-gray-600">{plan?.description}</p>
          <p className="text-gray-500 mt-2">
            Created: {plan?.created_at ? format(new Date(plan.created_at), 'MMM d, yyyy') : '-'}
          </p>
        </div>
      )}

      {/* Excel-Style Project Master Sheet */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Project Items</h3>
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center rounded-md bg-indigo-100 px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-200"
            disabled={loading || addingRow}
          >
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Row
          </button>
        </div>

        <div className="overflow-x-auto rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Row #
                </th>
                {fieldDefinitions.map((field) => (
                  <th 
                    key={field.id} 
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    {field.name}
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Completed
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {plan?.rows?.map((row) => {
                const isRowSaving = savingRowId === row.id;
                const isCompleted = row.completed || false;
                
                return (
                  <tr 
                    key={row.id} 
                    className={`${isCompleted ? 'bg-gray-50' : ''} ${isRowSaving ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <span className={isCompleted ? 'line-through text-green-600 decoration-green-600 decoration-2' : ''}>
                        {row.row_number}
                      </span>
                    </td>
                    
                    {/* Render field inputs in the order of the sorted field definitions */}
                    {fieldDefinitions.map((field) => (
                      <td key={field.id} className="px-4 py-4 text-sm text-gray-500">
                        <div className={isCompleted ? 'line-through text-green-600 decoration-green-600 decoration-2' : ''}>
                          {renderFieldInput(row, field)}
                        </div>
                      </td>
                    ))}
                    
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isCompleted}
                          onChange={() => handleToggleRowCompleted(row)}
                          disabled={isRowSaving}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </div>
                    </td>
                    
                    <td className="px-4 py-4 text-sm">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        disabled={isRowSaving || loading}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              
              {(!plan?.rows || plan.rows.length === 0) && (
                <tr>
                  <td 
                    colSpan={fieldDefinitions.length + 3} 
                    className="px-4 py-8 text-sm text-center text-gray-500"
                  >
                    No rows found. Click "Add Row" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ProjectPlanDetail;